import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";

import tailwind from "./styles/tailwind.css?url";
import { dirFor, t, type Locale } from "./i18n";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwind },
  { rel: "manifest", href: "/manifest.webmanifest" },
  { rel: "icon", type: "image/svg+xml", href: "/icons/icon.svg" },
  { rel: "apple-touch-icon", href: "/icons/icon-192.png" },
  // Preload Arabic font
  {
    rel: "preload",
    href: "/fonts/IBMPlexSansArabic-Regular.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
];

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale ?? "ar") as Locale;
  return [
    { title: t(locale, "app.name") },
    { name: "description", content: t(locale, "app.tagline") },
    { name: "theme-color", content: "#1f4e79" },
    { name: "color-scheme", content: "light" },
    { name: "robots", content: "noindex, nofollow" },
    { httpEquiv: "X-Content-Type-Options", content: "nosniff" },
    // iOS / Safari "Add to Home Screen" support — gives a fullscreen,
    // chrome-less PWA-like experience without needing a service worker.
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
    { name: "apple-mobile-web-app-title", content: "بنك الأسلحة" },
    { name: "mobile-web-app-capable", content: "yes" },
    { name: "format-detection", content: "telephone=no" },
  ];
};

export async function loader({ request: _request }: LoaderFunctionArgs) {
  // Arabic-only government system — never auto-switch to English based on
  // Accept-Language. Hard-pin to "ar" so the layout direction stays RTL
  // regardless of the operator's browser settings.
  const locale: Locale = "ar";
  return json({ locale });
}

export function Layout({ children }: { children: React.ReactNode }) {
  // The Layout function is shared between the normal render and the
  // ErrorBoundary render. `useLoaderData` is illegal in the error path
  // (Remix throws "You cannot useLoaderData in an errorElement"), so we
  // use `useRouteLoaderData("root")` which safely returns undefined.
  const data = useRouteLoaderData<typeof loader>("root");
  const locale = (data?.locale ?? "ar") as Locale;
  const dir = dirFor(locale);

  return (
    <html lang={locale} dir={dir}>
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=yes"
        />
        <Meta />
        <Links />
      </head>
      <body className="min-h-dvh bg-background text-foreground antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error = useRouteError();
  const isResponse = isRouteErrorResponse(error);
  const status = isResponse ? error.status : 500;
  const message = isResponse
    ? error.data?.message ?? error.statusText
    : error instanceof Error
      ? error.message
      : "حدث خطأ غير متوقع";

  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="gov-card max-w-md w-full p-8 text-center space-y-3">
        <div className="text-4xl font-bold text-danger">{status}</div>
        <h1 className="text-xl font-semibold">حدث خطأ</h1>
        <p className="text-muted-foreground text-sm">{message}</p>
        <a
          href="/"
          className="inline-block mt-4 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          العودة للرئيسية
        </a>
      </div>
    </main>
  );
}
